// Copyright © SRGTech (Software Resonance Group Inc.). All rights reserved.
// SPDX-License-Identifier: LicenseRef-SRGTech-Proprietary
// See LICENSE and docs/legal/PROPRIETARY_COPYRIGHT.md.

/**
 * How a college row and a lifecycle refusal are presented (**ADR-0076**).
 *
 * Pure functions, deliberately separated from the components that call them.
 * Three of the properties this screen has to hold are decidable from data
 * alone — which of the three lifecycles a cell is describing, whether an
 * operation is worth offering, and what an operator is told when the server
 * says no — and stating them as functions is what lets each be tested
 * exhaustively rather than through whichever row a render happened to produce.
 *
 * **The three lifecycles stay separate.** ADR-0076 §1 exists because
 * conflating them produced "claimed therefore active". So the tenant status,
 * the activation state, and the onboarding state each have their own table
 * here, and no function derives one from another. A college whose code has been
 * claimed is still `pending`, and this module has no way to say otherwise.
 *
 * **Eligibility here is presentation, never authorization.** `CST-048` and
 * `ADM-014` are explicit that the browser decides nothing. {@link isDeletable}
 * and {@link isDeactivatable} exist so an operator is not offered a control the
 * server is certain to refuse; the server refuses it anyway, and
 * {@link describeLifecycleFailure} is what makes that refusal legible.
 *
 * **No upstream text ever reaches a screen.** `API-012` keeps provider
 * messages, constraint names, and hostnames out of the browser, and a refusal
 * that quoted one would also be the first place an activation digest could
 * surface. Every string returned from here is a literal in this file.
 */

import {
  ApiProblemError,
  type ActivationSummaryView,
  type EdgeProvisioningState,
  type EdgeProvisioningView,
  type TenantOverview,
} from '@eai/ui-shared';

/** Badge emphasis shared by the three lifecycle columns. */
export type Tone = 'neutral' | 'positive' | 'caution' | 'negative';

/** A rendered lifecycle cell: what it says, and how strongly. */
export interface StatePresentation {
  readonly label: string;
  readonly tone: Tone;
}

/**
 * The central tenant lifecycle (**ADR-0067**).
 *
 * `pending` deliberately does not read as a completed onboarding. Registration
 * records eligibility and routing; it starts no appliance.
 */
export const TENANT_STATUS_PRESENTATION: Readonly<
  Record<TenantOverview['status'], StatePresentation>
> = {
  pending: { label: 'Registered — pending onboarding', tone: 'caution' },
  active: { label: 'Active', tone: 'positive' },
  deactivated: { label: 'Deactivated', tone: 'neutral' },
};

/**
 * The activation-code lifecycle (**ADR-0076** §1).
 *
 * `not_issued` is the state `SVRC` is in — registered before activation
 * existed, so it never received a code. It is shown as plainly as any other
 * state, because it is the one row an operator most needs to act on.
 */
export const ACTIVATION_STATE_PRESENTATION: Readonly<
  Record<ActivationSummaryView['state'], StatePresentation>
> = {
  not_issued: { label: 'Not issued', tone: 'caution' },
  active: { label: 'Issued — awaiting claim', tone: 'positive' },
  claimed: { label: 'Claimed', tone: 'positive' },
  expired: { label: 'Expired', tone: 'negative' },
  revoked: { label: 'Revoked', tone: 'negative' },
  not_applicable: { label: 'Not applicable', tone: 'neutral' },
};

/** How far a college's appliance has got (**ADR-0076** §1). */
export const ONBOARDING_STATE_PRESENTATION: Readonly<
  Record<NonNullable<TenantOverview['onboarding']>['state'], StatePresentation>
> = {
  activation_required: { label: 'Activation required', tone: 'caution' },
  configuration_required: { label: 'Configuration required', tone: 'caution' },
  verifying: { label: 'Verifying readiness', tone: 'neutral' },
  failed: { label: 'Readiness failed', tone: 'negative' },
  ready: { label: 'Ready', tone: 'positive' },
  deactivated: { label: 'Deactivated', tone: 'neutral' },
};

/** How far a college's Edge allocation has got (the provisioning bridge). */
export const EDGE_PROVISIONING_PRESENTATION: Readonly<
  Record<EdgeProvisioningState, StatePresentation>
> = {
  requested: { label: 'Provisioning queued', tone: 'neutral' },
  provisioning: { label: 'Provisioning', tone: 'neutral' },
  ready_for_activation: { label: 'Ready for activation', tone: 'positive' },
  failed: { label: 'Provisioning failed', tone: 'negative' },
  decommission_requested: { label: 'Decommissioning', tone: 'caution' },
  decommissioning: { label: 'Decommissioning', tone: 'caution' },
  decommissioned: { label: 'Decommissioned', tone: 'neutral' },
};

/** Why an allocation failed, in words an administrator can act on. */
export const EDGE_FAILURE_REASON_TEXT: Readonly<Record<string, string>> = {
  refused_by_edge: 'The Edge cluster refused this allocation.',
  edge_unreachable: 'The Edge cluster could not be reached.',
  operation_failed: 'Provisioning did not complete.',
  timed_out: 'Provisioning did not finish in time.',
  attempts_exhausted: 'Provisioning failed repeatedly and needs an operator.',
};

/**
 * What the Edge column says for one college.
 *
 * A college nobody has approved has no allocation, and the server returns
 * `null` rather than inventing a record. It reads *Not provisioned*, which is
 * the true statement and the one that makes the button beside it make sense.
 *
 * Deliberately separate from the onboarding column. An appliance can be
 * *Ready for activation* here while onboarding still reads *Activation
 * required*: the allocation exists and the college has not yet claimed. Showing
 * one value for both would be the "claimed therefore active" conflation
 * ADR-0076 exists to prevent.
 *
 * @param provisioning - The allocation, or `null` when none exists.
 * @returns The label and tone for the cell.
 */
export function describeEdgeProvisioning(
  provisioning: EdgeProvisioningView | null | undefined,
): StatePresentation {
  if (provisioning === null || provisioning === undefined) {
    return { label: 'Not provisioned', tone: 'neutral' };
  }
  return EDGE_PROVISIONING_PRESENTATION[provisioning.state];
}

/**
 * The sentence shown beneath a failed allocation.
 *
 * A bounded set the server publishes, mapped to bounded text. An unknown code
 * gets a generic sentence rather than being rendered raw: whatever arrives here
 * came from another cluster, and a screen is not the place to find out that the
 * vocabulary drifted.
 *
 * @param provisioning - The allocation.
 * @returns The sentence, or `null` when there is nothing to explain.
 */
export function describeEdgeFailure(
  provisioning: EdgeProvisioningView | null | undefined,
): string | null {
  const reason = provisioning?.failure_reason;
  if (!reason) {
    return null;
  }
  return EDGE_FAILURE_REASON_TEXT[reason] ?? 'Provisioning failed.';
}

/**
 * What the onboarding column says for one college.
 *
 * A college registered before activation existed has no onboarding row at all,
 * and `SVRC` is exactly that. It reads *Activation required*, which is both
 * true and the thing to do about it — the alternative, a blank cell, would hide
 * the college most in need of a code.
 *
 * @param onboarding - The summary the list carried, or `null` when absent.
 * @returns The label and tone for the cell.
 */
export function describeOnboarding(onboarding: TenantOverview['onboarding']): StatePresentation {
  if (onboarding === null) {
    return ONBOARDING_STATE_PRESENTATION.activation_required;
  }
  return ONBOARDING_STATE_PRESENTATION[onboarding.state];
}

/**
 * The masked identifier for a college's current code, if it has one.
 *
 * Four characters of a 140-bit value: enough to tell an operator which code a
 * row holds, and not enough to be one. Nothing else about a code is ever
 * rendered outside the one-time disclosure.
 *
 * @param activation - The non-secret activation summary.
 * @returns The masked tail, or `null` when no code is outstanding.
 */
export function maskedTail(activation: ActivationSummaryView): string | null {
  return activation.last_four === null ? null : `••••${activation.last_four}`;
}

/**
 * Render an instant for display.
 *
 * The value is passed through unchanged rather than localised. A locale-formatted
 * instant is ambiguous in a screenshot attached to an incident, and the screens
 * around this one already show the server's own representation.
 *
 * @param value - An ISO instant, or `null`.
 * @param absent - What to say when there is none.
 * @returns Display text.
 */
export function formatInstant(value: string | null, absent: string): string {
  return value ?? absent;
}

/** Expiry text for the list's expiry column. */
export function describeExpiry(activation: ActivationSummaryView): string {
  if (activation.state === 'not_issued') {
    return 'No code issued';
  }
  if (activation.state === 'not_applicable') {
    return 'Not applicable';
  }
  return formatInstant(activation.expires_at, 'Not reported');
}

/**
 * Whether generating a code would be the *first* one for this college.
 *
 * Only the wording differs. Both cases call the same rotation operation, and
 * the server decides what actually happens.
 */
export function isFirstIssuance(row: TenantOverview): boolean {
  return row.activation.state === 'not_issued';
}

/** The rotation control's label for one row. */
export function rotateActionLabel(row: TenantOverview): string {
  return isFirstIssuance(row) ? 'Generate activation code' : 'Regenerate activation code';
}

/**
 * Whether a fresh activation code can be issued for this college.
 *
 * Every college but a withdrawn one (**ADR-0076** §4g): re-enrolment is
 * supported, so a college that has already claimed its code may be issued
 * another — an operator rotating a credential after a suspected compromise, or
 * replacing an appliance to reconfigure it, needs exactly that.
 *
 * Withdrawal is the one refusal, and it is worth predicating on rather than
 * discovering: offering a control the server will always refuse teaches an
 * operator that the error is noise.
 */
export function isRotatable(row: TenantOverview): boolean {
  return row.status !== 'deactivated';
}

/**
 * Whether deactivation is worth offering for this college.
 *
 * A college already withdrawn cannot be withdrawn again. Everything else can:
 * deactivation always overrides readiness (**ADR-0076** §2), so a `pending`
 * college that is mid-enrolment is a legitimate target.
 */
export function isDeactivatable(row: TenantOverview): boolean {
  return row.status !== 'deactivated';
}

/**
 * Whether restricted deletion is worth offering for this college.
 *
 * Mirrors the narrow window ADR-0076 §4e permits — never claimed, never bound,
 * never active, never progressed past `activation_required` — so an operator is
 * not invited to attempt something the server will certainly refuse.
 *
 * It is a *mirror*, not the decision. Platform holds the claim, binding, and
 * replay history this row does not carry, and it answers `409` on its own
 * evidence. {@link describeLifecycleFailure} exists for exactly that answer.
 */
export function isDeletable(row: TenantOverview): boolean {
  if (row.status !== 'pending') {
    return false;
  }
  if (row.activation.state === 'claimed' || row.activation.claimed_at !== null) {
    return false;
  }
  return row.onboarding === null || row.onboarding.state === 'activation_required';
}

/**
 * Whether the evidence behind an onboarding row describes the current request.
 *
 * ADR-0076 §2 makes `active` derived from *generation-current* evidence, so an
 * observed generation behind the requested one is not a partial success — it is
 * evidence about a superseded request. Saying so is the difference between an
 * operator waiting and an operator re-issuing.
 *
 * @param onboarding - The onboarding summary.
 * @returns Operator-facing text describing the evidence's currency.
 */
export function describeReadinessEvidence(
  onboarding: NonNullable<TenantOverview['onboarding']>,
): string {
  if (onboarding.observed_generation === null) {
    return 'No evidence reported yet';
  }
  if (onboarding.observed_generation < onboarding.requested_generation) {
    return 'Stale — the evidence describes an earlier request';
  }
  return 'Current — the evidence describes this request';
}

/**
 * The outcome of readiness verification, stated in the operator's terms.
 *
 * Never "healthy by absence". `ADM-014` forbids presenting missing telemetry as
 * success, so a college that has reported nothing reads as not yet evaluated
 * rather than as passing.
 *
 * @param onboarding - The onboarding summary.
 * @returns Operator-facing outcome text.
 */
export function describeReadinessOutcome(
  onboarding: NonNullable<TenantOverview['onboarding']>,
): string {
  switch (onboarding.state) {
    case 'ready':
      return 'Every mandatory readiness condition passed';
    case 'failed':
      return 'Readiness was not confirmed';
    case 'verifying':
      return 'Evidence is being verified';
    case 'deactivated':
      return 'Withdrawn from service; readiness is not evaluated';
    default:
      return 'Not yet evaluated';
  }
}

/** The lifecycle operations a refusal can belong to. */
export type LifecycleOperation =
  'generate' | 'amend' | 'deactivate' | 'delete' | 'onboarding' | 'provision' | 'refresh';

const GENERIC_REFUSAL: Readonly<Record<LifecycleOperation, string>> = {
  generate: 'The activation code could not be generated. Try again.',
  amend: 'The college could not be updated. Try again.',
  deactivate: 'The college could not be deactivated. Try again.',
  delete: 'The college could not be deleted. Try again.',
  onboarding: 'The onboarding details could not be read. Try again.',
  provision: 'The Edge appliance could not be requested. Try again.',
  refresh: 'The college list could not be refreshed. Try again.',
};

const CONFLICT_REFUSAL: Readonly<Record<LifecycleOperation, string>> = {
  // The one conflict with a specific remedy. ADR-0076 §4e permits deletion only
  // for a registration that never enrolled, and the remedy for everything else
  // is deactivation — so the message says that rather than inviting a retry
  // that cannot succeed.
  delete:
    'This college cannot be deleted because it has been claimed or has progressed beyond activation. Deactivate it instead.',
  // Accurate for both refusals the server can still produce here, now that the
  // control is predicated on `isRotatable`: `rotation_ineligible` means someone
  // withdrew the college while this screen was open, and `rotation_conflict`
  // means someone rotated it concurrently. Both are races a refresh resolves.
  generate: 'This college changed while you were working on it. Refresh the list and try again.',
  // The server refuses a second approval while one is in flight or the
  // appliance already serves, so a conflict here means the row moved under the
  // operator rather than that anything is wrong.
  provision: 'This college changed while you were working on it. Refresh the list and try again.',
  amend: 'This college changed while you were working on it. Refresh the list and try again.',
  deactivate: 'This college changed while you were working on it. Refresh the list and try again.',
  onboarding: 'This college changed while you were working on it. Refresh the list and try again.',
  refresh: 'This college changed while you were working on it. Refresh the list and try again.',
};

/**
 * Translate a refusal into something an operator can act on.
 *
 * Never quotes the failure. `API-012` keeps upstream text out of the browser,
 * and a message assembled from a server string is the one place a digest, a
 * constraint name, or an internal hostname could reach a screen. Every branch
 * returns a literal from this module.
 *
 * A refusal is also never treated as impossible. `403` reaching a control the
 * capability check allowed is not a contradiction — the session's capabilities
 * are an affordance and the server is the authority (`CST-048`) — so it has its
 * own plain answer rather than falling through to "try again".
 *
 * @param error - Whatever the client rejected with.
 * @param operation - Which lifecycle operation was attempted.
 * @returns Operator-facing text, safe to render.
 */
export function describeLifecycleFailure(error: unknown, operation: LifecycleOperation): string {
  if (!(error instanceof ApiProblemError)) {
    return GENERIC_REFUSAL[operation];
  }
  switch (error.status) {
    case 401:
      return 'Your session has ended. Sign in again to continue.';
    case 403:
      return 'The service refused this operation for your account. Nothing was changed.';
    case 404:
      return 'This college is no longer registered. Refresh the list.';
    case 409:
      return CONFLICT_REFUSAL[operation];
    case 422:
      return 'The service did not accept these values. Check them and try again.';
    case 429:
      return 'The service is limiting requests. Wait a moment and try again.';
    case 503:
    case 504:
      return 'The service is unavailable. Try again shortly.';
    default:
      return GENERIC_REFUSAL[operation];
  }
}

/**
 * Whether the administrator may usefully press provision for one college.
 *
 * A button that cannot work is worse than no button, so this refuses the same
 * cases the server refuses: an allocation already in flight or serving, and one
 * being stood down. A failed allocation is offered as *Retry* while attempts
 * remain, which is the documented way out of a failure.
 *
 * Deactivated and deleted colleges are excluded outright. Standing a college
 * down and building it an appliance are contradictory instructions, and the
 * lifecycle already refuses the combination -- offering it here would only move
 * the refusal to somewhere the operator has already committed.
 *
 * @param row - The college row.
 * @param provisioning - Its allocation, or `null` when it has none.
 * @returns Whether to show the button.
 */
export function canProvisionFor(
  row: TenantOverview,
  provisioning: EdgeProvisioningView | null,
): boolean {
  if (row.status !== 'active' && row.status !== 'pending') {
    return false;
  }
  if (provisioning === null) {
    return true;
  }
  return provisioning.state === 'failed' && provisioning.can_retry;
}
